/**
 * Who may authorize a visitor — the rules, and how a choice reads before it is saved.
 *
 * A deliberate mirror of microservices/condo-manager/services/visitor_authorizer_policy.go, and
 * visitorAuthorizer.test.ts reads that Go file to prove the two still agree. The server is the
 * authority; this exists so the settings screen can say what a choice will actually do.
 */

export interface VisitorAuthorizerConfig {
  enabled: boolean;
  /** Chosen relation types. EMPTY MEANS EVERYBODY — the state the screen is in when the switch
   *  is first flipped, and a switch that locks the building before a choice is made would be
   *  indistinguishable from a bug. */
  relations: string[];
}

/** What the backend reports about this customer's population. */
export interface VisitorAuthorizerImpact {
  total: number;
  by_relation: Record<string, number>;
  /** People whose relation type was never set — the ones a choice silently affects. */
  unlabelled: number;
  /** People already carrying the per-person exception, who keep the ability regardless. */
  with_flag: number;
}

/**
 * A condomínio's relations are to a UNIT, an empresa's to a DEPARTAMENTO, and the two
 * vocabularies genuinely differ: a department has a responsável and funcionários and no
 * dependentes. Offering the wrong word is how an operator chooses a relation nobody in their
 * customer can hold.
 *
 * Mirrors condoAuthorizerRelations / empresaAuthorizerRelations in the Go policy, and the labels
 * match the two option lists already used elsewhere in the web (ROLE_RELATION_OPTIONS and
 * DEPARTMENT_RELATION_OPTIONS).
 */
const CONDO_RELATIONS: { value: string; label: string }[] = [
  { value: 'proprietario', label: 'Proprietário' },
  { value: 'inquilino', label: 'Inquilino' },
  { value: 'dependente', label: 'Dependente' },
  { value: 'familiar', label: 'Familiar' },
];

const EMPRESA_RELATIONS: { value: string; label: string }[] = [
  { value: 'proprietario', label: 'Proprietário' },
  { value: 'inquilino', label: 'Inquilino' },
  { value: 'responsavel', label: 'Responsável' },
  { value: 'funcionario', label: 'Funcionário' },
];

export const visitorAuthorizerRelations = (
  isEmpresa: boolean,
): { value: string; label: string }[] => (isEmpresa ? EMPRESA_RELATIONS : CONDO_RELATIONS);

export const visitorAuthorizerRelationLabel = (relation: string, isEmpresa: boolean): string =>
  visitorAuthorizerRelations(isEmpresa).find((o) => o.value === relation)?.label ?? relation;

/** Folds case and accents, like the Go policy — real rows carry every spelling. */
export const normalizeAuthorizerRelation = (relation: string): string =>
  (relation ?? '')
    .trim()
    .toLowerCase()
    .replace(/[áàãâ]/g, 'a')
    .replace(/[éê]/g, 'e')
    .replace(/[í]/g, 'i')
    .replace(/[óôõ]/g, 'o')
    .replace(/[ú]/g, 'u')
    .replace(/[ç]/g, 'c');

/**
 * The same four gates the server applies, in the same order. Used to render a person's state in
 * the modal rather than to decide anything — the decision is the server's.
 */
export const canAuthorizeVisitors = (
  config: VisitorAuthorizerConfig,
  relation: string,
  personFlag: boolean,
): boolean => {
  if (!config.enabled) return true;
  if (personFlag) return true;
  const chosen = config.relations.map(normalizeAuthorizerRelation).filter(Boolean);
  if (chosen.length === 0) return true;
  return chosen.includes(normalizeAuthorizerRelation(relation));
};

/**
 * How many people the current choice would leave able to authorize visitors.
 *
 * THE POINT OF THIS FUNCTION: once a relation is chosen, somebody whose relation type was never
 * set is refused. In a customer whose rows are mostly unlabelled that can take the ability from
 * almost everybody, so the number belongs on screen beside the switch rather than being
 * discovered afterwards by a resident standing at a gate.
 *
 * `withFlag` is added rather than intersected because the backend counts it as a distinct
 * population; a person who is BOTH flagged and of a chosen relation would be counted twice, so
 * the result is clamped to the total. Erring toward the total is the safe direction: it can
 * understate how many people are affected, never overstate how many are fine.
 */
export const visitorAuthorizerAllowedCount = (
  impact: VisitorAuthorizerImpact | null,
  config: VisitorAuthorizerConfig,
): number => {
  if (!impact) return 0;
  if (!config.enabled) return impact.total;

  const chosen = config.relations.map(normalizeAuthorizerRelation).filter(Boolean);
  if (chosen.length === 0) return impact.total;

  const byRelation = chosen.reduce(
    (sum, relation) => sum + (impact.by_relation[relation] ?? 0),
    0,
  );
  return Math.min(impact.total, byRelation + impact.with_flag);
};

/**
 * The sentence shown under the picker.
 *
 * Written as a plain count rather than a percentage, and it names the unlabelled group
 * explicitly, because "34 pessoas sem tipo definido ficarão sem a permissão" is the one fact an
 * operator has to see before saving. Silence there is what turns a deliberate setting into a
 * support call.
 */
export const visitorAuthorizerSummary = (
  impact: VisitorAuthorizerImpact | null,
  config: VisitorAuthorizerConfig,
): string => {
  if (!impact) return '';
  if (!config.enabled) {
    return 'Qualquer pessoa com acesso ao app pode liberar visitantes.';
  }

  const chosen = config.relations.map(normalizeAuthorizerRelation).filter(Boolean);
  if (chosen.length === 0) {
    return 'Nenhum perfil selecionado — por enquanto, qualquer pessoa pode liberar visitantes.';
  }

  const allowed = visitorAuthorizerAllowedCount(impact, config);
  const people = impact.total === 1 ? 'pessoa' : 'pessoas';
  let sentence = `${allowed} de ${impact.total} ${people} poderão liberar visitantes.`;

  if (impact.unlabelled > 0) {
    const them = impact.unlabelled === 1 ? 'pessoa' : 'pessoas';
    const verb = impact.unlabelled === 1 ? 'ficará' : 'ficarão';
    sentence +=
      ` ${impact.unlabelled} ${them} sem tipo de vínculo definido ${verb} sem a permissão` +
      ' — defina o vínculo no cadastro ou libere individualmente.';
  }
  if (impact.with_flag > 0) {
    const them = impact.with_flag === 1 ? 'pessoa' : 'pessoas';
    sentence += ` ${impact.with_flag} ${them} com liberação individual mantêm a permissão.`;
  }
  return sentence;
};

/** Whether the choice is worth warning about in a louder tone than a sentence. */
export const visitorAuthorizerIsDrastic = (
  impact: VisitorAuthorizerImpact | null,
  config: VisitorAuthorizerConfig,
): boolean => {
  if (!impact || !config.enabled || impact.total === 0) return false;
  if (config.relations.length === 0) return false;
  // Half the building losing something they do daily deserves more than a grey line of text.
  return visitorAuthorizerAllowedCount(impact, config) * 2 < impact.total;
};
